import type { ChangeEvent } from "react";
import { X as XIcon } from "lucide-react";
import type { Layout, Sheet } from "../../types";
import {
  SHEET_PRESETS,
  CUSTOM_PRESET_ID,
  matchPreset,
} from "../../lib/constants/sheet-presets";

interface Props {
  layout: Layout | null;
  unplaced: string[];
  partLabelById: Map<string, string>;
  sheets: Sheet[];
  setSheets: (next: Sheet[]) => void;
  kerf: number;
  setKerf: (next: number) => void;
  edgePadding: number;
  setEdgePadding: (next: number) => void;
  isRunning: boolean;
}

/**
 * Summary + Settings panel.
 *
 * Top: live numbers (waste %, sheets, cost, cut length) — hero view for
 * E-07. Shows an unplaced-parts banner (E-13) when parts don't fit.
 *
 * Bottom: Settings — sheet-size preset + custom dimensions, kerf,
 * edge padding (E-05).
 */
export function SummaryPanel(props: Props) {
  const {
    layout,
    unplaced,
    partLabelById,
    sheets,
    setSheets,
    kerf,
    setKerf,
    edgePadding,
    setEdgePadding,
    isRunning,
  } = props;

  // Cost = sum over all sheet templates, weighted by how many copies of
  // each were actually used. Current packer places greedily into the
  // first template with remaining capacity, so we approximate by
  // distributing sheetsUsed across templates in order.
  const cost = layout ? computeTotalCost(sheets, layout.sheetsUsed) : null;

  const updateSheet = (i: number, patch: Partial<Sheet>) => {
    const next = sheets.slice();
    next[i] = { ...next[i]!, ...patch };
    setSheets(next);
  };

  const addSheet = () => {
    setSheets([
      ...sheets,
      {
        id: `s${Date.now()}`,
        width: 2440,
        height: 1220,
        quantity: 5,
      },
    ]);
  };

  const removeSheet = (i: number) => {
    if (sheets.length <= 1) return;
    setSheets(sheets.filter((_, idx) => idx !== i));
  };

  const unplacedLabels = unplaced
    .slice(0, 3)
    .map((id) => {
      const family = id.split("#")[0] ?? id;
      return partLabelById.get(family) ?? family;
    });
  const extraCount = unplaced.length - unplacedLabels.length;

  return (
    <div className="space-y-6">
      {/* Unplaced banner (E-13) */}
      {unplaced.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
        >
          <p className="font-semibold">
            {unplaced.length} part{unplaced.length === 1 ? "" : "s"} didn't fit
          </p>
          <p className="mt-1 text-xs">
            {unplacedLabels.join(", ")}
            {extraCount > 0 ? ` + ${extraCount} more` : ""}
          </p>
          <p className="mt-2 text-xs opacity-80">
            Try: add more sheets, unlock rotation, or shrink the part.
          </p>
        </div>
      )}

      {/* Summary (E-07) */}
      <section aria-label="Layout summary">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Summary
        </h2>
        {layout ? (
          <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <SummaryStat
              label="Waste"
              value={`${layout.wastePercent.toFixed(1)}%`}
              hero
            />
            <SummaryStat label="Sheets" value={`${layout.sheetsUsed}`} />
            <SummaryStat
              label="Cost"
              value={cost == null ? "—" : `$${cost.toFixed(0)}`}
            />
            <SummaryStat
              label="Cut length"
              value={`${Math.round(layout.totalCutLength)} mm`}
            />
          </dl>
        ) : (
          <p className="mt-3 text-sm text-slate-500">
            {isRunning ? "Optimizing…" : "No layout yet. Add parts + click Optimize."}
          </p>
        )}
      </section>

      {/* Settings (E-05 + O-05 multi-sheet) */}
      <section aria-label="Cutting settings">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Settings
        </h2>
        <div className="mt-3 space-y-3">
          <div>
            <p className="mb-1 text-xs text-slate-500">
              Sheet stock ({sheets.length} {sheets.length === 1 ? "type" : "types"})
            </p>
            <ul className="space-y-2">
              {sheets.map((sheet, i) => (
                <li key={sheet.id}>
                  <SheetRow
                    sheet={sheet}
                    canRemove={sheets.length > 1}
                    onChange={(patch) => updateSheet(i, patch)}
                    onRemove={() => removeSheet(i)}
                  />
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn-ghost mt-2 w-full text-xs"
              onClick={addSheet}
            >
              + Add sheet type
            </button>
          </div>

          <div>
            <label className="text-xs text-slate-500" htmlFor="kerf">
              Blade width (kerf, mm)
            </label>
            <input
              id="kerf"
              type="number"
              className="input"
              min={0}
              value={kerf}
              onChange={(e) => setKerf(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500" htmlFor="edge-padding">
              Edge padding (mm)
            </label>
            <input
              id="edge-padding"
              type="number"
              className="input"
              min={0}
              value={edgePadding}
              onChange={(e) => setEdgePadding(Number(e.target.value))}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

interface SheetRowProps {
  sheet: Sheet;
  canRemove: boolean;
  onChange: (patch: Partial<Sheet>) => void;
  onRemove: () => void;
}

function SheetRow({ sheet, canRemove, onChange, onRemove }: SheetRowProps) {
  const presetId = matchPreset(sheet.width, sheet.height);
  const isCustom = presetId === CUSTOM_PRESET_ID;

  const handlePreset = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    if (id === CUSTOM_PRESET_ID) return;
    const preset = SHEET_PRESETS.find((p) => p.id === id);
    if (!preset) return;
    onChange({ width: preset.width, height: preset.height });
  };

  return (
    <div className="rounded border border-slate-200 p-2 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <select
          className="input flex-1"
          aria-label="Sheet preset"
          value={presetId}
          onChange={handlePreset}
        >
          {SHEET_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
          <option value={CUSTOM_PRESET_ID}>Custom…</option>
        </select>
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label="Remove sheet type"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 dark:hover:bg-red-950"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      {isCustom && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="number"
            aria-label="Sheet width"
            className="input w-20"
            value={sheet.width}
            min={1}
            onChange={(e) => onChange({ width: Number(e.target.value) })}
          />
          <span className="text-xs">×</span>
          <input
            type="number"
            aria-label="Sheet height"
            className="input w-20"
            value={sheet.height}
            min={1}
            onChange={(e) => onChange({ height: Number(e.target.value) })}
          />
        </div>
      )}

      <div className="mt-2 flex items-center gap-2 text-xs">
        <label className="flex items-center gap-1 text-slate-500">
          qty
          <input
            type="number"
            aria-label="Sheet quantity"
            className="input w-14"
            value={sheet.quantity}
            min={1}
            onChange={(e) => onChange({ quantity: Number(e.target.value) })}
          />
        </label>
        <label className="flex items-center gap-1 text-slate-500">
          cost ($)
          <input
            type="number"
            aria-label="Sheet cost per unit"
            className="input w-20"
            value={sheet.costPerUnit ?? ""}
            min={0}
            placeholder="—"
            onChange={(e) => {
              const v = e.target.value === "" ? undefined : Number(e.target.value);
              onChange({ costPerUnit: v });
            }}
          />
        </label>
      </div>
    </div>
  );
}

/**
 * Distribute sheetsUsed across the templates in order (mirrors the
 * packer's "first template with capacity" strategy) and sum the cost
 * of each used sheet times its costPerUnit. Sheets without a cost are
 * skipped; if any used sheet lacks a cost, return null so the UI
 * shows "—" instead of an incomplete number.
 */
function computeTotalCost(sheets: Sheet[], sheetsUsed: number): number | null {
  let remaining = sheetsUsed;
  let total = 0;
  let anyMissing = false;
  for (const s of sheets) {
    if (remaining <= 0) break;
    const used = Math.min(s.quantity, remaining);
    remaining -= used;
    if (s.costPerUnit == null) {
      if (used > 0) anyMissing = true;
      continue;
    }
    total += used * s.costPerUnit;
  }
  if (anyMissing && total === 0) return null;
  if (total === 0) return null;
  return total;
}

function SummaryStat({
  label,
  value,
  hero = false,
}: {
  label: string;
  value: string;
  hero?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={
          hero
            ? "font-mono text-2xl font-bold text-slate-900 dark:text-slate-100"
            : "font-mono text-base text-slate-800 dark:text-slate-200"
        }
      >
        {value}
      </dd>
    </div>
  );
}
